import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { LuNetwork, LuShuffle } from 'react-icons/lu'

import logoBlack from '@/assets/img/logo.svg'
import logoWhite from '@/assets/img/logo-inverse.svg'
import CompanyTypeIcon from '@/components/misc/CompanyIcon/CompanyTypeIcon'
import CompanyLogoOrName from '@/components/misc/CompanyLogoOrName'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import useAppSelector from '@/hooks/useAppSelector'
import { useAuth } from '@/hooks/useAuth'
import useMaxWidth from '@/hooks/useMaxWidth'
import { faCalculatorSimple } from '@/lib/fontAwesomePro/light'
import authService from '@/lib/services/authService'
import {
  selectCurrentNetwork,
  selectUserNetworks,
} from '@/redux/slices/session'

interface Props {
  children: React.ReactNode
  companyName?: string | null
  companyLogo?: string | null
  isDarkMode?: boolean
}

const MinimalLayout: React.FC<Props> = ({
  children,
  companyName = null,
  companyLogo = null,
  isDarkMode = true,
}) => {
  const isMaxWidthMd = useMaxWidth('md')

  const {
    isAuthenticated,
    companies,
    groups,
    currentCompany,
    currentGroup,
    updateCompanyContext,
    updateGroupContext,
    updateNetworkContext,
  } = useAuth()
  const networks = useAppSelector(selectUserNetworks)
  const currentNetwork = useAppSelector(selectCurrentNetwork)

  const otherGroups = currentGroup
    ? groups.filter((group) => group['@id'] !== currentGroup['@id'])
    : groups

  const otherNetworks = currentNetwork
    ? networks.filter((network) => network['@id'] !== currentNetwork['@id'])
    : networks

  const otherCompanies = currentCompany
    ? companies.filter((company) => company['@id'] !== currentCompany['@id'])
    : companies

  const hasOtherAccounts =
    otherNetworks.length > 0 ||
    otherGroups.length > 0 ||
    otherCompanies.length > 0

  const showBranding = companyName !== null

  return (
    <>
      <div className="flex items-start justify-between p-4 md:p-6">
        {showBranding ? (
          <CompanyLogoOrName
            name={companyName}
            logoUrl={companyLogo}
            showLogoFallback={false}
            wrapperProps={{
              className: isDarkMode ? 'text-white' : 'text-black',
            }}
            imageStyle={{
              height: isMaxWidthMd ? '1.5rem' : '2rem',
              maxWidth: '110px',
            }}
          />
        ) : (
          <img
            src={isDarkMode ? logoWhite : logoBlack}
            style={{ height: isMaxWidthMd ? '1.5rem' : '2rem' }}
            alt="RQ"
          />
        )}

        <div className="flex flex-wrap items-start justify-end gap-2 self-end">
          {hasOtherAccounts && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline-white">
                  <LuShuffle />
                  Switch account
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="max-h-175 overflow-y-auto"
              >
                {otherNetworks.length > 0 && (
                  <>
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>Network</DropdownMenuLabel>

                      {otherNetworks.map((network) => (
                        <DropdownMenuItem
                          key={network.id}
                          onClick={() =>
                            updateNetworkContext({ network: network['@id'] })
                          }
                        >
                          <LuNetwork size={16} />
                          {network.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}

                {otherGroups.length > 0 && (
                  <>
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>Group</DropdownMenuLabel>

                      {otherGroups.map((group) => (
                        <DropdownMenuItem
                          key={group.id}
                          onClick={() =>
                            updateGroupContext({ group: group['@id'] })
                          }
                        >
                          <CompanyTypeIcon
                            companyTypeCode="other"
                            style={{ width: '16px', height: '16px' }}
                          />
                          {group.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}

                {otherCompanies.length > 0 && (
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Company</DropdownMenuLabel>
                    {otherCompanies.map((company) => (
                      <DropdownMenuItem
                        onClick={() =>
                          updateCompanyContext({ company: company['@id'] })
                        }
                        key={company.id + company.companyType.code}
                      >
                        <FontAwesomeIcon icon={faCalculatorSimple} />
                        {company.presentationName}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {isAuthenticated && (
            <Button
              className="inline"
              variant="outline-white"
              onClick={() => authService.logout()}
            >
              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
            </Button>
          )}
        </div>
      </div>

      <div
        className="mt-4 px-0 sm:px-4"
        style={{
          width: '100%',
          maxWidth: '870px',
          marginInline: 'auto',
        }}
      >
        {children}
      </div>
    </>
  )
}

export default MinimalLayout
